import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ForwardedRef, ReactNode } from "react";
import { classNames } from "../utils/classNames";

export type IconButtonSize = "sm" | "md" | "lg";
export type IconButtonTone = "neutral" | "accent" | "danger";

export type IconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label" | "children"
> & {
  "aria-label": string;
  children: ReactNode;
  size?: IconButtonSize;
  tone?: IconButtonTone;
  isLoading?: boolean;
  loadingLabel?: string;
};

function IconButtonComponent(
  {
    "aria-label": ariaLabel,
    children,
    className,
    disabled = false,
    isLoading = false,
    loadingLabel = "正在处理",
    size = "md",
    tone = "neutral",
    type = "button",
    ...props
  }: IconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-label={ariaLabel}
      aria-busy={isLoading || undefined}
      className={classNames(
        "herskin-icon-button",
        `herskin-icon-button--${size}`,
        `herskin-icon-button--${tone}`,
        isLoading && "herskin-icon-button--loading",
        className,
      )}
    >
      <span
        className={classNames(
          "herskin-icon-button__icon",
          isLoading && "herskin-icon-button__icon--hidden",
        )}
        aria-hidden="true"
      >
        {children}
      </span>
      {isLoading ? (
        <span className="herskin-icon-button__loading">
          <span className="herskin-spinner" aria-hidden="true" />
          <span className="herskin-sr-only">{loadingLabel}</span>
        </span>
      ) : null}
    </button>
  );
}

export const IconButton = forwardRef(IconButtonComponent);
IconButton.displayName = "IconButton";
